
import { defineComponent } from "vue";
import { required, maxLength, minLength } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import messages from "@/config/messages";
import routesPath from "@/config/routesPath";
import * as apiClient from "@/apiclient/";
import vueRecaptcha from "vue3-recaptcha2";
import ToolTip from "@/components/common/ToolTip.vue";
import TokenService from "@/services/tokenService";
import store from "@/store";

interface User {
  userName: string;
  securityQuestion: string;
  securityAnswer: string;
  gRecaptcha: string;
  code: string;
}

export default defineComponent({
  name: "ForgotPasswordView",
  components: {
    vueRecaptcha,
    ToolTip,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      user: {
        userName: "",
        securityQuestion: "",
        securityAnswer: "",
        gRecaptcha: "",
        code: "",
      } as User,
      step: 1,
      isFormInvalid: false,
      validationMsg: "You entry was invalid. Please try again.",
      isSubmitDisabled: false,
      responseMessage: "An email has been sent with instructions.",
      lastDateMsg: "",
      isResponseValid: false,
      messages,
      routesPath,
      googleRecaptchaKey: store.state.settings.googleRecaptchaKey?? "",
      showRecaptcha: false,
      requireChallenge: false,
      challengeToken: "",
      buttonLabel: "Next",
      captchaToken: "",
      validatedCaptchaToken: "",
      emailSent: false,
      loadingTimeout: 30000,
      credentials: [] as apiClient.CredentialDetails[],
      selectedCredential: 0,
      selectedCredentialText: "",
      credentialSelected: false,
      challengeInstructions: "",
      challengeQuestionId: 0,
    };
  },
  validations() {
    return {
      user: {
        userName: {
          required,
        },
        securityAnswer: {
          required,
        },
        code: {
          required,
        },
      },
    };
  },
  methods: {
    backButtonHandler() {
      if (this.step === 2) {
        this.step = 1;
        this.showRecaptcha = false;
        this.buttonLabel = "Next";
      } else {
        this.$router.push({
          path: routesPath.base,
        });
      }
    },
    async activeCallBack() {
      if (this.step === 1) {
        this.doUserNameCheck();
      }
      if (this.step === 2) {
        this.validateCaptcha();
      }
      if (this.step === 3) {
        this.selectCredential();
      }
      if (this.step === 4) {
        this.validateCode();
      }
    },
    async doUserNameCheck() {
      //get our items for the step 1 account check
      if (this.v$.user.userName.$invalid) return;
      this.isFormInvalid = false;
      this.validationMsg = "";
      const params: apiClient.ForgotPasswordParameters = {
        username: this.user.userName,
      };
      this.isSubmitDisabled = true;
      const result = await apiClient
        .loginController()
        .postApiLoginForgotpassword(params);
      this.isSubmitDisabled = false;

      if (!result) {
        this.isFormInvalid = true;
        this.validationMsg = "An error has occured, please try again";
        return;
      }

      //determine how to proceed here based on the response
      if (result.data!.validUser) {
        let token: string = result.data!.captchaToken || "";
        this.captchaToken = token;
        this.showRecaptcha = true;
        this.buttonLabel = "Validate";
        this.step = 2;
      } else {
        this.isFormInvalid = true;
        const noMatchMessage: string = result.data?.message || "";
        this.validationMsg = noMatchMessage;
        return;
      }
    },
    async validateCaptcha() {
      this.isSubmitDisabled = true;
      const result = await apiClient
        .forgotController()
        .postApiForgotValidatecaptcha(this.user.gRecaptcha, {
          headers: {
            Authorization: "Bearer " + this.captchaToken,
            "Content-Type": "application/json",
          },
        });
      this.isSubmitDisabled = false;
      if (!result.data!.valid) {
        this.isFormInvalid = true;
        this.validationMsg = "Please try again. Your response was invalid";
        return;
      }
      this.buttonLabel = "";
      this.validatedCaptchaToken = result.data!.validatedCaptchaToken!;
      this.showRecaptcha = false;
      this.credentials = result.data!.credentialDetails!;
      this.step = 3;
      this.buttonLabel = "Continue";
    },
    async selectCredential() {
      if (this.selectedCredential == 0) {
        return;
      }
      const result = await apiClient
        .forgotController()
        .postApiForgotSelectcredential(this.selectedCredential, {
          headers: {
            Authorization: "Bearer " + this.validatedCaptchaToken,
            "Content-Type": "application/json",
          },
        });
      this.isSubmitDisabled = false;

      if (!result.data!.valid) {
        this.isFormInvalid = true;
        this.validationMsg = "Please try again. Your response was invalid";
        return;
      }
      this.challengeInstructions = result.data!.challengeInstructions || "";
      this.challengeQuestionId = result.data!.challengeQuestionId || 0;
      this.step = 4;
      this.buttonLabel = "Continue";
    },
    async validateCode() {
      if (this.v$.user.code.$invalid) return;

      var params = {
        credentialId: this.selectedCredential,
        questionId: this.challengeQuestionId,
        code: this.user.code,
      };
      const result = await apiClient
        .forgotController()
        .postApiForgotValidatecode(params, {
          headers: {
            Authorization: "Bearer " + this.validatedCaptchaToken,
            "Content-Type": "application/json",
          },
        });
      this.isSubmitDisabled = false;

      if (!result.data!.valid) {
        this.isFormInvalid = true;
        this.validationMsg = "Please try again. Your response was invalid";
        return;
      }
      if (result.data!.expired) {
        this.isFormInvalid = true;
        this.validationMsg = "Please try again. Your code expired";
        return;
      }
      let token: string = result.data!.resetPasswordToken || "";
      TokenService.setToken("ResetPasswordToken", token);

      this.$router.push({
        path: routesPath.resetPassword,
      });
    },
    async doForgotPassword() {
      if (this.requireChallenge && !this.showRecaptcha) {
        if (this.v$.user.securityAnswer.$invalid) return;

        this.isSubmitDisabled = true;
        const result = await apiClient
          .forgotController()
          .postApiForgotValidatechallenge(this.user.securityAnswer, {
            headers: {
              Authorization: "Bearer " + this.challengeToken,
              "Content-Type": "application/json",
            },
          });
        this.isSubmitDisabled = false;
        if (!result.data!.valid) {
          this.isFormInvalid = true;
          this.validationMsg = "Please try again. Your response was invalid";
          return;
        }
        let token: string = result.data!.captchaToken || "";
        this.captchaToken = token;
        this.showRecaptcha = true;
        this.buttonLabel = "Validate";
      }

      if (this.showRecaptcha) {
        if (this.user.gRecaptcha != "") {
          this.isSubmitDisabled = true;
          const result = await apiClient
            .forgotController()
            .postApiForgotValidatecaptcha(this.user.gRecaptcha, {
              headers: {
                Authorization: "Bearer " + this.captchaToken,
                "Content-Type": "application/json",
              },
            });
          this.isSubmitDisabled = false;
          if (!result.data!.valid) {
            this.isFormInvalid = true;
            this.validationMsg = "Please try again. Your response was invalid";
            return;
          }

          this.emailSent = true;
        }
      }
    },
    recaptchaVerified(response: string): void {
      this.isSubmitDisabled = false;
      if (response) {
        this.user.gRecaptcha = response;
      }
    },
    recaptchaExpired(): void {
      if (vueRecaptcha.value) {
        vueRecaptcha.value.reset();
      }
      this.user.gRecaptcha = "";
    },
    recaptchaFailed(): void {
      this.user.gRecaptcha = "";
    },
    recaptchaError(reason: any): void {
      this.user.gRecaptcha = "";
    },
    hideErrors(field: string): void {
      this.v$[field].$touch();
      this.isFormInvalid = false;
    },

    buttonDesign(): string {
      const buttonStatus = this.v$.user.userName.$invalid;
      return buttonStatus == true ? "btn-secondary" : "btn-primary";
    },
  },
});
